import { supabase } from './supabase';
import { Database } from './database.types';

type User = Database['public']['Tables']['users']['Row'];
type TeamPermission = Database['public']['Tables']['team_permissions']['Row'];

export interface OrganizationMember {
  id: string;
  userId: string;
  name: string;
  email: string;
  avatar?: string;
  teamRole: string;
  organizationId: string;
  joinedAt: string;
  lastActive?: string | null;
  creditsUsed?: number;
  permissions: Record<string, boolean>;
}

export interface RolePermissions {
  [roleName: string]: {
    [permission: string]: boolean;
  };
}

export class TeamRoleService {
  /**
   * Update member role in organization
   */
  static async updateMemberRole(userId: string, newRole: 'admin' | 'editor' | 'viewer'): Promise<void> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      // Check if current user has permission to update roles
      const { data: currentUser, error: currentUserError } = await supabase
        .from('users')
        .select('team_role, organization_id')
        .eq('id', user.id)
        .single();

      if (currentUserError || !currentUser) {
        throw new Error('Unable to verify user permissions');
      }

      if (currentUser.team_role !== 'admin') {
        throw new Error('Only admins can update member roles');
      }

      // Get target user to verify they're in same organization
      const { data: targetUser, error: targetUserError } = await supabase
        .from('users')
        .select('organization_id')
        .eq('id', userId)
        .single();

      if (targetUserError || !targetUser) {
        throw new Error('Target user not found');
      }

      if (targetUser.organization_id !== currentUser.organization_id) {
        throw new Error('User is not in your organization');
      }

      // Update the user's role
      const { error } = await supabase
        .from('users')
        .update({ team_role: newRole })
        .eq('id', userId);

      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('Update member role error:', error);
      throw error;
    }
  }

  /**
   * Get team members for organization with owner info
   */
  static async getTeamMembers(organizationId: string): Promise<{ members: OrganizationMember[], ownerId: string | null }> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      // Get organization details including owner
      const { data: organization, error: orgError } = await supabase
        .from('organizations')
        .select('created_by')
        .eq('id', organizationId)
        .single();

      if (orgError) {
        console.error('Error fetching organization:', orgError);
      }

      // Get all users in the organization
      const { data: members, error } = await supabase
        .from('users')
        .select('*')
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false });

      if (error) {
        throw error;
      }

      // Get permissions for each role
      const rolePermissions = await this.getAllRolePermissions();

      // Transform users to organization members
      const organizationMembers: OrganizationMember[] = await Promise.all(
        (members || []).map(async (member) => {
          const permissions = rolePermissions[member.team_role || 'viewer'] || {};
          
          // TODO: Get actual credits used and last active data
          const creditsUsed = await this.getMemberCreditsUsed(member.id);
          const lastActive = await this.getMemberLastActive(member.id);

          return {
            id: member.id,
            userId: member.id,
            name: member.name,
            email: member.email,
            avatar: member.avatar || undefined,
            teamRole: member.team_role || 'viewer',
            organizationId: member.organization_id || '',
            joinedAt: member.created_at || '',
            lastActive,
            creditsUsed,
            permissions
          };
        })
      );

      return {
        members: organizationMembers,
        ownerId: organization?.created_by || null
      };
    } catch (error) {
      console.error('Get team members error:', error);
      throw error;
    }
  }

  /**
   * Remove member from organization
   */
  static async removeMemberFromTeam(userId: string): Promise<void> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      // Check if current user has permission to remove members
      const { data: currentUser, error: currentUserError } = await supabase
        .from('users')
        .select('team_role, organization_id')
        .eq('id', user.id)
        .single();

      if (currentUserError || !currentUser) {
        throw new Error('Unable to verify user permissions');
      }

      if (currentUser.team_role !== 'admin') {
        throw new Error('Only admins can remove team members');
      }

      // Prevent removing self
      if (userId === user.id) {
        throw new Error('You cannot remove yourself from the organization');
      }

      // Get target user to verify they're in same organization
      const { data: targetUser, error: targetUserError } = await supabase
        .from('users')
        .select('organization_id')
        .eq('id', userId)
        .single();

      if (targetUserError || !targetUser) {
        throw new Error('Target user not found');
      }

      if (targetUser.organization_id !== currentUser.organization_id) {
        throw new Error('User is not in your organization');
      }

      // Remove user from organization
      const { error } = await supabase
        .from('users')
        .update({ 
          organization_id: null,
          team_role: 'viewer'
        })
        .eq('id', userId);

      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('Remove member from team error:', error);
      throw error;
    }
  }

  /**
   * Get all role permissions
   */
  static async getAllRolePermissions(): Promise<RolePermissions> {
    try {
      const { data: permissions, error } = await supabase
        .from('team_permissions')
        .select('*');

      if (error) {
        throw error;
      }

      const rolePermissions: RolePermissions = {};

      permissions?.forEach((perm) => {
        if (!rolePermissions[perm.role]) {
          rolePermissions[perm.role] = {};
        }
        rolePermissions[perm.role][perm.permission] = perm.can_perform || false;
      });

      return rolePermissions;
    } catch (error) {
      console.error('Get role permissions error:', error);
      throw error;
    }
  }

  /**
   * Check if user has specific permission
   */
  static async hasPermission(userId: string, permission: string): Promise<boolean> {
    try {
      const { data: user, error } = await supabase
        .from('users')
        .select('team_role')
        .eq('id', userId)
        .single();

      if (error || !user) {
        return false;
      }

      const { data: permissionData, error: permissionError } = await supabase
        .from('team_permissions')
        .select('can_perform')
        .eq('role', user.team_role || 'viewer')
        .eq('permission', permission)
        .single();

      if (permissionError || !permissionData) {
        return false;
      }

      return permissionData.can_perform || false;
    } catch (error) {
      console.error('Check permission error:', error);
      return false;
    }
  }

  /**
   * Validate role permissions for specific action
   */
  static async validateRolePermission(action: string): Promise<boolean> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        return false;
      }

      return await this.hasPermission(user.id, action);
    } catch (error) {
      console.error('Validate role permission error:', error);
      return false;
    }
  }

  /**
   * Get available roles and their descriptions
   */
  static getAvailableRoles(): Array<{
    value: string;
    label: string;
    description: string;
  }> {
    return [
      {
        value: 'admin',
        label: 'Admin',
        description: 'Full access to all features including team management'
      },
      {
        value: 'editor',
        label: 'Editor',
        description: 'Can analyze, upload, and manage projects but cannot manage team'
      },
      {
        value: 'viewer',
        label: 'Viewer',
        description: 'Basic access to view reports and analyze creatives'
      }
    ];
  }

  /**
   * Get member credits used (placeholder for actual implementation)
   */
  private static async getMemberCreditsUsed(userId: string): Promise<number> {
    // TODO: Implement actual credits usage calculation
    // This would query analyses/activities for this user and calculate credits used
    try {
      // First get the user's creatives
      const { data: creatives, error: creativesError } = await supabase
        .from('creatives')
        .select('id')
        .eq('user_id', userId);

      if (creativesError || !creatives) {
        return 0;
      }

      // Then get analyses for those creatives
      const creativeIds = creatives.map(c => c.id);
      
      if (creativeIds.length === 0) {
        return 0;
      }

      const { data: analyses, error: analysesError } = await supabase
        .from('analyses')
        .select('id')
        .in('creative_id', creativeIds);

      return analyses?.length || 0;
    } catch (error) {
      console.error('Get member credits used error:', error);
      return 0;
    }
  }

  /**
   * Get member last active timestamp as ISO date string for proper date handling
   */
  private static async getMemberLastActive(userId: string): Promise<string | null> {
    try {
      const { data: activity, error } = await supabase
        .from('activity_log')
        .select('created_at')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (activity?.created_at) {
        return activity.created_at;
      }

      return null;
    } catch (error) {
      console.error('Get member last active error:', error);
      return null;
    }
  }
} 